"use client";

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";

import type { PaidPlan } from "@/lib/billing/planConfig";

type UpgradeSuccessModalContextValue = {
  isOpen: boolean;
  /** Which tier was just purchased — drives the modal copy. */
  plan: PaidPlan;
  /** The tier the user came from — the "before" side of the modal's diff. */
  from: "free" | PaidPlan;
  /** Increments on every open — a key that resets the modal's activation poll. */
  openCount: number;
  open: (plan?: PaidPlan, from?: "free" | PaidPlan) => void;
  close: () => void;
};

const UpgradeSuccessModalContext =
  createContext<UpgradeSuccessModalContextValue | null>(null);

export function UpgradeSuccessModalProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [plan, setPlan] = useState<PaidPlan>("pro");
  const [from, setFrom] = useState<"free" | PaidPlan>("free");
  const [openCount, setOpenCount] = useState(0);

  const open = useCallback((p: PaidPlan = "pro", f: "free" | PaidPlan = "free") => {
    setPlan(p);
    setFrom(f);
    setOpenCount((n) => n + 1);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  return (
    <UpgradeSuccessModalContext.Provider value={{ isOpen, plan, from, openCount, open, close }}>
      {children}
    </UpgradeSuccessModalContext.Provider>
  );
}

export function useUpgradeSuccessModal(): UpgradeSuccessModalContextValue {
  const ctx = useContext(UpgradeSuccessModalContext);
  if (!ctx) {
    throw new Error(
      "useUpgradeSuccessModal must be used within UpgradeSuccessModalProvider",
    );
  }
  return ctx;
}
